import React, { useState } from "react";
import { Info, Plus, Minus, HelpCircle } from "lucide-react";

/**
 * Tooltip
 * -------
 * Small info icon next to a label that reveals contextual help on hover / focus.
 * Uses only Tailwind — no external tooltip lib needed. Renders below the anchor
 * by default; the containing element must have `position: relative` for correct
 * positioning (all form rows below do).
 */
export function FieldTooltip({ children, testId }) {
  return (
    <span className="inline-block relative group" data-testid={testId}>
      <HelpCircle
        size={12}
        className="text-ink-muted hover:text-emerald-600 cursor-help ml-1 inline-block align-middle"
      />
      <span className="pointer-events-none absolute z-30 left-0 top-6 w-64 bg-navy text-white text-[11px] px-3 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
        {children}
      </span>
    </span>
  );
}

/**
 * FieldWithHelp
 * -------------
 * Labeled input with optional tooltip + optional nudge/hint below the field.
 * Signals to the founder/investor exactly what to enter and why it matters.
 */
export function FieldWithHelp({
  label, tooltip, nudge, testId, children,
}) {
  return (
    <label className="block">
      {label && (
        <div className="overline mb-1.5 flex items-center">
          <span>{label}</span>
          {tooltip && <FieldTooltip>{tooltip}</FieldTooltip>}
        </div>
      )}
      {children}
      {nudge && (
        <div className="text-[11px] text-ink-muted mt-1 italic flex items-start gap-1" data-testid={testId ? `${testId}-nudge` : undefined}>
          <Info size={11} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <span>{nudge}</span>
        </div>
      )}
    </label>
  );
}

/**
 * NumberTicker
 * ------------
 * Numeric input with +/- stepper buttons + live-formatted preview.
 * `format(value)` is used to render the preview label — e.g. INR crores/lakhs.
 */
export function NumberTicker({
  value, onChange, step = 1, min = 0, max, placeholder, testId, format,
}) {
  const num = Number(value) || 0;
  const set = (v) => {
    if (min != null && v < min) v = min;
    if (max != null && v > max) v = max;
    onChange(v === 0 ? "" : v);
  };
  const bump = (delta) => set(num + delta);

  return (
    <div>
      <div className="flex items-stretch gap-1.5">
        <button
          type="button"
          onClick={() => bump(-step)}
          className="w-9 flex-shrink-0 rounded-md border border-navy/15 text-ink-muted hover:text-emerald-700 hover:border-emerald-300 flex items-center justify-center transition-colors"
          data-testid={testId ? `${testId}-decrement` : undefined}
          tabIndex={-1}
        >
          <Minus size={13} />
        </button>
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          placeholder={placeholder}
          min={min}
          max={max}
          step={step}
          data-testid={testId}
          className="w-full min-w-0 bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm font-body focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition-all placeholder-ink-faint rounded-md"
        />
        <button
          type="button"
          onClick={() => bump(step)}
          className="w-9 flex-shrink-0 rounded-md border border-navy/15 text-ink-muted hover:text-emerald-700 hover:border-emerald-300 flex items-center justify-center transition-colors"
          data-testid={testId ? `${testId}-increment` : undefined}
          tabIndex={-1}
        >
          <Plus size={13} />
        </button>
      </div>
      {format && num > 0 && (
        <div className="text-[11px] text-emerald-700 font-mono mt-1" data-testid={testId ? `${testId}-preview` : undefined}>
          = {format(num)}
        </div>
      )}
    </div>
  );
}

/**
 * SearchableSelect
 * ----------------
 * Combobox that lets the user type-to-filter or free-type an option not in
 * the list. Used for City & Country pickers.
 */
export function SearchableSelect({
  value, onChange, options, placeholder, testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  React.useEffect(() => { setQuery(value || ""); }, [value]);
  const filtered = (options || []).filter(
    (o) => o.toLowerCase().includes((query || "").toLowerCase())
  );
  const commit = (v) => {
    onChange({ target: { value: v } });
    setQuery(v);
    setOpen(false);
  };
  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); onChange({ target: { value: e.target.value } }); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
        className="w-full bg-white border border-navy/15 text-ink px-3 py-2.5 text-sm font-body focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition-all placeholder-ink-faint rounded-md"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-white border border-navy/15 rounded-md shadow-lg">
          {filtered.slice(0, 20).map((opt) => (
            <button
              key={opt}
              type="button"
              onMouseDown={() => commit(opt)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-navy/5 last:border-0"
              data-testid={`${testId}-opt-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
